import {
    useInfiniteQuery,
    useQuery,
    type DefaultError,
    type InfiniteData,
    type QueryKey,
    type UseInfiniteQueryOptions,
    type UseInfiniteQueryResult,
    type UseQueryOptions,
    type UseQueryResult,
} from '@tanstack/react-query';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectIsLoggedIn } from 'flow/selectors/app-selectors';

/** `useQuery` for endpoints that need a session — without one the request 401s and tears the app down. */
export const useAuthenticatedQuery = <
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
>(
    options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError> => {
    const isLoggedIn = useAppSelector(selectIsLoggedIn);

    return useQuery<TQueryFnData, TError, TData, TQueryKey>({
        ...options,
        // `enabled` may be a predicate in v5, so select rather than `&&`.
        enabled: isLoggedIn ? (options.enabled ?? true) : false,
    });
};

/** As {@link useAuthenticatedQuery}, for paged endpoints. */
export const useAuthenticatedInfiniteQuery = <
    TQueryFnData,
    TError = DefaultError,
    TData = InfiniteData<TQueryFnData>,
    TQueryKey extends QueryKey = QueryKey,
    TPageParam = unknown,
>(
    options: UseInfiniteQueryOptions<
        TQueryFnData,
        TError,
        TData,
        TQueryKey,
        TPageParam
    >,
): UseInfiniteQueryResult<TData, TError> => {
    const isLoggedIn = useAppSelector(selectIsLoggedIn);

    return useInfiniteQuery<TQueryFnData, TError, TData, TQueryKey, TPageParam>(
        {
            ...options,
            enabled: isLoggedIn ? (options.enabled ?? true) : false,
        },
    );
};
